import { createSlice, createSelector } from '@reduxjs/toolkit';

/**
 * Filter Slice V2 - Simplified filter state management
 *
 * This slice stores only the essential filter state:
 * - filterId: unique identifier for the filter
 * - selectedValue: the currently selected value (integer ID)
 * - selectedLabel: the display label for the selected value
 * - enabled: whether the filter is enabled/disabled
 */

const initialState = {
  filters: {}, // Key: filterId, Value: { selectedValue, selectedLabel, enabled, loading, options }
};

const filterSliceV2 = createSlice({
  name: 'filtersV2',
  initialState,
  reducers: {
    /**
     * Set or update a filter's state
     * @param {Object} action.payload - { filterId, selectedValue?, selectedLabel?, enabled? }
     */
    setFilter: (state, action) => {
      const { filterId, selectedValue, selectedLabel, enabled } =
        action.payload;

      if (!state.filters[filterId]) {
        state.filters[filterId] = {
          selectedValue: null,
          selectedLabel: null,
          enabled: true,
          loading: false,
        };
      }

      if (selectedValue !== undefined) {
        state.filters[filterId].selectedValue = selectedValue;
      }

      if (selectedLabel !== undefined) {
        state.filters[filterId].selectedLabel = selectedLabel;
      }

      if (enabled !== undefined) {
        state.filters[filterId].enabled = enabled;
      }

      if (action.payload.loading !== undefined) {
        state.filters[filterId].loading = action.payload.loading;
      }

      if (action.payload.options !== undefined) {
        state.filters[filterId].options = action.payload.options;
      }
    },

    /**
     * Set the selected value for a specific filter
     * @param {Object} action.payload - { filterId, selectedValue, selectedLabel? }
     */
    setFilterValue: (state, action) => {
      const { filterId, selectedValue, selectedLabel } = action.payload;

      if (!state.filters[filterId]) {
        state.filters[filterId] = {
          selectedValue: null,
          selectedLabel: null,
          enabled: true,
          loading: false,
        };
      }

      state.filters[filterId].selectedValue = selectedValue;

      if (selectedLabel !== undefined) {
        state.filters[filterId].selectedLabel = selectedLabel;
      }
    },

    /**
     * Enable or disable a specific filter
     * @param {Object} action.payload - { filterId, enabled }
     */
    setFilterEnabled: (state, action) => {
      const { filterId, enabled } = action.payload;

      if (!state.filters[filterId]) {
        state.filters[filterId] = {
          selectedValue: null,
          selectedLabel: null,
          enabled: true,
          loading: false,
        };
      }

      state.filters[filterId].enabled = enabled;
    },

    /**
     * Set the loading state for a specific filter
     * @param {Object} action.payload - { filterId, loading }
     */
    setFilterLoading: (state, action) => {
      const { filterId, loading } = action.payload;

      if (!state.filters[filterId]) {
        state.filters[filterId] = {
          selectedValue: null,
          selectedLabel: null,
          enabled: true,
          loading: false,
        };
      }

      state.filters[filterId].loading = loading;
    },
  },
});

// Action creators
export const { setFilter, setFilterValue, setFilterEnabled, setFilterLoading } =
  filterSliceV2.actions;

// Selectors
export const selectAllFilters = (state) => {
  return state?.filtersV2?.filters || {};
};

export const selectFilter = (state, filterId) => {
  return state?.filtersV2?.filters?.[filterId] || null;
};

export const selectFilterValue = (state, filterId) => {
  return state?.filtersV2?.filters?.[filterId]?.selectedValue ?? null;
};

export const selectFilterLabel = (state, filterId) => {
  return state?.filtersV2?.filters?.[filterId]?.selectedLabel ?? null;
};

export const selectFilterEnabled = (state, filterId) => {
  return state?.filtersV2?.filters?.[filterId]?.enabled ?? true;
};

export const selectFilterLoading = (state, filterId) => {
  return state?.filtersV2?.filters?.[filterId]?.loading ?? false;
};

export const selectFilterOptions = (state, filterId) => {
  return state?.filtersV2?.filters?.[filterId]?.options ?? [];
};

// Memoized selector to prevent creating new object references on every call
export const selectEnabledFilters = createSelector(
  [selectAllFilters],
  (filters) => {
    return Object.entries(filters)
      .filter(([, filter]) => filter.enabled)
      .reduce((acc, [filterId, filter]) => {
        acc[filterId] = filter;
        return acc;
      }, {});
  }
);

export default filterSliceV2.reducer;
